// "Where to go after login". An anonymous visitor who opens a deep link (most
// importantly `/extension?ext_challenge=…`, the link the Chrome extension
// opens) is sent to /login; without this the destination — and the PKCE
// challenge in its query — was lost and login always landed on /inbox.
//
// Only paths inside this app are ever accepted: the value can arrive from the
// URL (`/login?next=…`), so it is treated as untrusted (open-redirect guard).

const KEY = 'od.return_to';
const BASE = 'http://od.internal';
const NEVER = new Set(['/login', '/setup']);

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Returns a safe in-app `path?query`, or null for anything else (external URLs, `//host`, schemes, /login). */
export function sanitizeReturnTo(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2000) return null;
  // Must be an absolute path. `//host` and `/\host` are protocol-relative URLs in browsers.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, BASE);
  } catch {
    return null;
  }
  if (url.origin !== BASE) return null;
  if (NEVER.has(url.pathname)) return null;
  return url.pathname + url.search;
}

export function rememberReturnTo(raw: string): void {
  const safe = sanitizeReturnTo(raw);
  if (!safe) return;
  try {
    storage()?.setItem(KEY, safe);
  } catch {
    // storage unavailable (private mode quota etc.) — login simply lands on the default page.
  }
}

/** Reads and forgets the remembered destination. Re-validated on the way out as well. */
export function consumeReturnTo(): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    s.removeItem(KEY);
    return sanitizeReturnTo(raw);
  } catch {
    return null;
  }
}

export function clearReturnTo(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}
